import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { t } from '../../i18n.js';
import { api, authState } from '../../state/auth.js';
import { plan, loadPlan } from '../../utils/plan.js';
import Button from './Button.jsx';
import Modal from './Modal.jsx';
import { DepartmentLabel } from './DepartmentLabel.jsx';

const ERRORS = {
  name_exists: 'Deze naam bestaat al.',
  invalid_color: 'Kies een geldige kleur.',
  validation_error: 'Naam is verplicht (maximaal 60 tekens).',
  too_many_departments: 'Meer dan 20 afdelingen kan niet.',
  invalid_logo: 'Het logo moet een PNG, JPG of WebP zijn van maximaal 2 MB.',
};
const LOGO_ACCEPT = 'image/png,image/jpeg,image/webp';
const euro = n => `€${n}`;

// Afdelingen of an omnivereniging (voetbal, handbal, honkbal): add, edit, remove. The
// second one changes the bill, so the admin sees the price first (the server answers
// confirm_required with it) and confirms; below Professional the club moves up right away.
export default function DepartmentsCard({ departments, sections, onChanged }) {
  const [modal, setModal] = useState(null); // null | { mode: 'create'|'edit', dept? }
  const [name, setName] = useState('');
  const [color, setColor] = useState('#2563eb');
  const [logo, setLogo] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [price, setPrice] = useState(null); // the confirm_required detail
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => { loadPlan(`${authState.value.userId}:${authState.value.activeClubId}`); }, []);
  const surcharge = plan.value?.departments?.surchargeMonthly;
  const sectionCount = dept => sections.filter(s => s.department_id === dept.id).length;

  const open = (mode, dept = null) => {
    setName(dept?.name || '');
    setColor(dept?.color || '#2563eb');
    setLogo(null);
    setRemoveLogo(false);
    setPrice(null);
    setError(null);
    setModal({ mode, dept });
  };

  // confirmedCount: the number of departments in the price the admin agreed to
  const save = async (confirmedCount = null) => {
    if (!name.trim()) { setError('Naam is verplicht'); return; }
    setSaving(true);
    setError(null);
    const form = new FormData();
    form.append('name', name);
    form.append('color', color);
    if (logo) form.append('logo', logo);
    try {
      if (modal.mode === 'create') {
        if (confirmedCount) form.append('confirmedCount', String(confirmedCount));
        const result = await api('/api/settings/departments', { method: 'POST', body: form, headers: {} });
        // A new plan: menus and pages load their data per plan, so start fresh
        if (result.upgradedFrom) { window.location.reload(); return; }
      } else {
        if (removeLogo && !logo) form.append('removeLogo', 'true');
        await api(`/api/settings/departments/${modal.dept.id}`, { method: 'PUT', body: form, headers: {} });
      }
      setModal(null);
      onChanged();
    } catch (err) {
      // A new price (another admin added one meanwhile) is shown again before anything happens
      if (err.code === 'confirm_required') setPrice(err.detail);
      else setError(ERRORS[err.code] || 'Opslaan mislukt.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await api(`/api/settings/departments/${confirmDelete.id}`, { method: 'DELETE' });
      setConfirmDelete(null);
      onChanged();
    } catch {
      setDeleteError('Verwijderen mislukt. Probeer het opnieuw.');
    } finally {
      setDeleting(false);
    }
  };

  const currentTier = plan.value?.tierLabel;
  const movesUp = price && currentTier && price.tierLabel !== currentTier;
  const afterDelete = departments.length - 1;

  return (
    <div class="card" style="padding:0;overflow:hidden;margin-top:16px">
      <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap;padding:14px 16px;background:var(--color-bg-secondary);border-bottom:1px solid var(--color-border)">
        <div style="font-weight:700;font-size:15px">Afdelingen</div>
        <Button variant="secondary" size="sm" onClick={() => open('create')}>+ Afdeling toevoegen</Button>
      </div>
      <div style="padding:12px 16px">
        <p style="font-size:13px;color:var(--color-text-muted);margin:0 0 10px;line-height:1.5">
          Heeft jullie vereniging meer sporten (een omnivereniging)? Maak dan per sport een afdeling en koppel er de secties aan.
          Secties zonder afdeling horen bij de hoofdvereniging, zoals het hoofdbestuur en de accommodatie.
          {surcharge != null && ` De eerste afdeling zit in jullie pakket. Vanaf de tweede is het Professional, met ${euro(surcharge)} per maand voor elke extra afdeling.`}
        </p>
        {departments.length > 0 && (
          <div style="display:flex;flex-direction:column;gap:8px">
            {departments.map(dept => (
              <div key={dept.id} style="display:flex;align-items:center;gap:10px;flex-wrap:wrap">
                <DepartmentLabel department={dept} />
                <span style="font-size:12px;color:var(--color-text-muted);flex:1">
                  {sectionCount(dept) === 1 ? '1 sectie' : `${sectionCount(dept)} secties`}
                </span>
                <Button variant="secondary" size="sm" onClick={() => open('edit', dept)}>Bewerken</Button>
                <Button variant="danger" size="sm" onClick={() => setConfirmDelete(dept)}>Verwijderen</Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal isOpen={!!modal} title={modal?.mode === 'create' ? 'Nieuwe afdeling' : 'Afdeling bewerken'} onClose={() => setModal(null)}>
        {price ? (
          <div>
            <p style="margin:0 0 12px;line-height:1.6">
              <strong>{name.trim()}</strong> wordt jullie {price.count}e afdeling. Vanaf twee afdelingen is het pakket {price.tierLabel}:
            </p>
            <div style="padding:12px 14px;border:1px solid var(--color-border);border-radius:8px;background:var(--color-bg-secondary);font-size:14px;line-height:1.7">
              <div>{price.tierLabel} met de eerste afdeling: {euro(price.tierPriceMonthly)}</div>
              <div>{price.extra} extra {price.extra === 1 ? 'afdeling' : 'afdelingen'} × {euro(price.surchargeMonthly)}: {euro(price.extra * price.surchargeMonthly)}</div>
              <div style="font-weight:700;margin-top:4px">Samen {euro(price.totalMonthly)} per maand (excl. btw)</div>
            </div>
            {price.withdrawsDowngrade && (
              <p style="margin:12px 0 0;font-size:13px;color:var(--color-text-secondary);line-height:1.5">
                De geplande overstap naar {price.withdrawsDowngrade.toLabel} gaat dan niet door: met twee of meer afdelingen blijft het pakket {price.tierLabel}.
              </p>
            )}
            {movesUp && (
              <p style="margin:12px 0 0;font-size:13px;color:var(--color-text-secondary);line-height:1.5">
                Jullie pakket gaat direct van {currentTier} naar {price.tierLabel}. Alle modules van {price.tierLabel} zijn dan meteen beschikbaar.
              </p>
            )}
            {error && <div class="alert alert-error mt-3">{error}</div>}
            <div class="flex gap-2 justify-end mt-4">
              <Button variant="secondary" onClick={() => setPrice(null)}>Terug</Button>
              <Button variant="primary" onClick={() => save(price.count)} disabled={saving}>
                {saving ? t('common.loading') : 'Akkoord, afdeling toevoegen'}
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <div class="form-group">
              <label class="form-label">Naam *</label>
              <input class="form-input" type="text" value={name} maxLength={60} onInput={e => setName(e.target.value)} placeholder="bijv. Handbal" autoFocus />
            </div>
            <div class="form-group" style="margin-top:14px">
              <label class="form-label">Kleur</label>
              <div style="display:flex;align-items:center;gap:10px">
                <input type="color" value={color} onInput={e => setColor(e.target.value)} style="width:44px;height:32px;padding:0;border:1px solid var(--color-border);border-radius:6px;background:none;cursor:pointer" aria-label="Kleur van de afdeling" />
                <DepartmentLabel department={{ name: name.trim() || 'Voorbeeld', color, logo_path: logo ? null : (removeLogo ? null : modal?.dept?.logo_path) }} />
              </div>
            </div>
            <div class="form-group" style="margin-top:14px">
              <label class="form-label">Logo (optioneel)</label>
              {modal?.dept?.logo_path && !removeLogo && !logo && (
                <div style="display:flex;align-items:center;gap:10px;margin-bottom:8px">
                  <img src={modal.dept.logo_path} alt="" style="width:32px;height:32px;object-fit:contain;border:1px solid var(--color-border);border-radius:6px" />
                  <Button variant="secondary" size="sm" onClick={() => setRemoveLogo(true)}>Logo verwijderen</Button>
                </div>
              )}
              <input type="file" accept={LOGO_ACCEPT} onChange={e => setLogo(e.target.files?.[0] || null)} />
              <p style="font-size:11px;color:var(--color-text-muted);margin-top:6px">PNG, JPG of WebP, maximaal 2 MB. Te zien naast de naam van de afdeling.</p>
            </div>
            {error && <div class="alert alert-error mb-3">{error}</div>}
            <div class="flex gap-2 justify-end mt-4">
              <Button variant="secondary" onClick={() => setModal(null)}>{t('common.cancel')}</Button>
              <Button variant="primary" onClick={() => save()} disabled={saving}>
                {saving ? t('common.loading') : t('common.save')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal isOpen={!!confirmDelete} title="Afdeling verwijderen" onClose={() => { setConfirmDelete(null); setDeleteError(null); }}>
        <p style="margin-bottom:12px;color:var(--color-text-muted);line-height:1.5">
          Weet je zeker dat je afdeling <strong>{confirmDelete?.name}</strong> wilt verwijderen? De secties blijven bestaan en horen daarna bij de hoofdvereniging.
        </p>
        {departments.length > 1 && surcharge != null && (
          <p style="margin-bottom:20px;color:var(--color-text-muted);line-height:1.5">
            {afterDelete > 1
              ? `Jullie houden ${afterDelete} afdelingen; de toeslag gaat ${euro(surcharge)} per maand omlaag.`
              : `Jullie houden één afdeling: de toeslag vervalt. Het pakket blijft ${currentTier || 'Professional'}.`}
          </p>
        )}
        {deleteError && <div class="alert alert-error mb-3">{deleteError}</div>}
        <div class="flex gap-2 justify-end">
          <Button variant="secondary" onClick={() => { setConfirmDelete(null); setDeleteError(null); }}>{t('common.cancel')}</Button>
          <Button variant="danger" onClick={remove} disabled={deleting}>
            {deleting ? t('common.loading') : t('common.delete')}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
